import { useEffect, useState, type FormEvent } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { apiGet, DEFAULT_AVATAR } from "../api/client";

type MeUser = {
  first_name?: string;
  last_name?: string;
  name?: string;
  email?: string;
  title?: string;
  profile_url?: string | null;
  bio?: { phone_number?: string };
};

/**
 * Historical settings shell (settings.html) + personal tab form
 * (tabs/personal-settings.html). Notifications tab is a stub section.
 */
export function SettingsPage() {
  const location = useLocation();
  const isNotifications = location.pathname.includes("/notifications");

  const [user, setUser] = useState<MeUser | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    apiGet<{ user?: MeUser }>("/me").then((me) => {
      const u = me?.user ?? null;
      setUser(u);
      setFirstName(u?.first_name || "");
      setLastName(u?.last_name || "");
      setEmail(u?.email || "");
    });
  }, []);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    // Demo archive: no mutation — mirror Angular success chrome.
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  }

  const displayName =
    [firstName, lastName].filter(Boolean).join(" ") || user?.name || "";

  return (
    <>
      <header className="ne__page-header">
        <div className="ne__page-header__provider">
          {/* Historical settings.html wraps photo in upload label */}
          <label className="ne__patient__upload" title="Upload new picture">
            {/* style.css sets input[type=file]{display:block} — force hide like Angular upload chrome */}
            <input
              type="file"
              accept="image/*"
              readOnly
              aria-hidden
              tabIndex={-1}
              className="ne__patient__upload-input"
              style={{ display: "none" }}
            />
            <div className="ne__patient__photo">
              <div className="ne__avatar ne__patient__avatar">
                <img
                  src={user?.profile_url || DEFAULT_AVATAR}
                  alt={displayName}
                />
                <div className="ne__patient__update-photo">
                  <i className="ne__icon__camera--fill ne__patient__update-photo-icon" />
                  <span className="ne__patient__update-photo-text">
                    Upload new picture
                  </span>
                </div>
              </div>
            </div>
          </label>
          <div className="ne__patient__details">
            <h2 className="ne__page-header__name">{displayName}</h2>
            <table className="ne__patient__info">
              <tbody>
                {email ? (
                  <tr>
                    <th>
                      <div className="ne__page-header__eyebrow">Email</div>
                    </th>
                    <td>
                      <div className="ne__page-header__data">{email}</div>
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
        <ul className="ne__tab__group">
          <li
            className={`ne__tab${!isNotifications ? " active" : ""}`}
          >
            <NavLink to="/app/settings/personal" className="ne__tab__link">
              <i className="ne__icon__user--thin" />
              <span className="ne__tab__label">Personal info</span>
            </NavLink>
          </li>
          <li className={`ne__tab${isNotifications ? " active" : ""}`}>
            <NavLink to="/app/settings/notifications" className="ne__tab__link">
              <i className="ne__icon__mail--thin" />
              <span className="ne__tab__label">Notifications</span>
            </NavLink>
          </li>
        </ul>
      </header>

      <section className="ne__settings__tabs-view">
        {isNotifications ? (
          <>
            <header className="ne__header">
              <h3 className="ne__header__heading">Notifications</h3>
              <p className="ne__header__subhead">
                Choose how you want to be notified about patient activity.
              </p>
            </header>
            <p className="ne__settings__placeholder">
              Notification preferences are view-only in this archive demo.
            </p>
          </>
        ) : (
          <>
            <header className="ne__header">
              <h3 className="ne__header__heading">Personal information</h3>
              <p className="ne__header__subhead">
                You can update the basic information of your own account here.
              </p>
            </header>
            <form onSubmit={onSubmit}>
              <div className="ne__fieldset__group">
                {/*
                  Mirror Angular ng-class on filled inputs so historical style.css
                  paints the cyan tick (ng-valid:not(.ng-empty)) and float labels
                  (ng-not-empty ~ .ne__placeholder).
                */}
                <fieldset className="ne__fieldset">
                  <label>
                    <input
                      className={`ne__input${firstName ? " has-value ng-not-empty ng-valid" : " ng-empty"}`}
                      type="text"
                      required
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    <span className="ne__placeholder">First Name</span>
                  </label>
                </fieldset>
                <fieldset className="ne__fieldset">
                  <label>
                    <input
                      className={`ne__input${lastName ? " has-value ng-not-empty ng-valid" : " ng-empty"}`}
                      type="text"
                      required
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                    <span className="ne__placeholder">Last name</span>
                  </label>
                </fieldset>
                <fieldset className="ne__fieldset">
                  <label>
                    <input
                      className={`ne__input${email ? " has-value ng-not-empty ng-valid" : " ng-empty"}`}
                      type="text"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <span className="ne__placeholder">Email address</span>
                  </label>
                </fieldset>
                <fieldset className="ne__fieldset">
                  <label>
                    <input
                      className={`ne__input${password ? " has-value ng-not-empty ng-valid" : " ng-empty"}`}
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <span className="ne__placeholder">Password</span>
                  </label>
                </fieldset>
                <fieldset className="ne__fieldset">
                  <label>
                    <input
                      className={`ne__input${passwordConfirm ? " has-value ng-not-empty ng-valid" : " ng-empty"}`}
                      type="password"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      autoComplete="new-password"
                    />
                    <span className="ne__placeholder">Password confirmation</span>
                  </label>
                </fieldset>
              </div>
              <div className="ne__btn__group">
                <button
                  className="ne__btn ne__btn--lg ne__btn--primary"
                  type="submit"
                >
                  {saved ? "Saved" : "Save"}
                </button>
              </div>
            </form>
          </>
        )}
      </section>
    </>
  );
}
